import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import {
  getDailyPageErrorMessage,
  getDailyPagePayload,
  getWeeklyMilestones,
  isDailyPageApiSuccess,
  putWeeklyMilestones,
} from '../../services/dailyPageApi';
import { DEFAULT_MILESTONES } from './dailyTasksUtils';

function BonusMilestonesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [milestones, setMilestones] = useState(DEFAULT_MILESTONES);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getWeeklyMilestones();
      if (!isDailyPageApiSuccess(res)) {
        setError(getDailyPageErrorMessage(res, 'Failed to load milestones.'));
        return;
      }
      const data = getDailyPagePayload(res) || {};
      const list = Array.isArray(data.milestones) ? data.milestones : [];
      setMilestones(
        list.length
          ? list.map((m, i) => ({
              days_required: Number(m.days_required ?? 0),
              fan_energy_reward: Number(m.fan_energy_reward ?? 0),
              title: m.title || '',
              message: m.message || '',
              sort_order: Number(m.sort_order ?? i + 1),
              is_active: m.is_active !== false,
            }))
          : DEFAULT_MILESTONES
      );
    } catch (err) {
      setError(err.message || 'Failed to load milestones.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = (index, patch) => {
    setMilestones((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body = {
        milestones: milestones.map((m, i) => ({
          days_required: Number(m.days_required) || 0,
          fan_energy_reward: Number(m.fan_energy_reward) || 0,
          title: m.title.trim(),
          message: m.message.trim(),
          sort_order: Number(m.sort_order) || i + 1,
          is_active: !!m.is_active,
        })),
      };
      const res = await putWeeklyMilestones(body);
      if (!isDailyPageApiSuccess(res)) {
        setError(getDailyPageErrorMessage(res, 'Failed to save milestones.'));
        return;
      }
      setSnackbar({
        open: true,
        message: 'Milestones saved. Changing FE does not re-pay fans who already claimed.',
        severity: 'success',
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Cumulative <strong>counted days</strong> on each fan’s personal 7-day track (not calendar
        Mon–Sun). A day counts only when <strong>all groups that day</strong> are complete. Missed
        days do not erase progress, but <strong>block the 7-day milestone</strong> for that cycle.
        Changing FE here does not re-pay fans who already claimed a milestone.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            setMilestones((prev) => [
              ...prev,
              {
                days_required: 1,
                fan_energy_reward: 0,
                title: '',
                message: '',
                sort_order: prev.length + 1,
                is_active: true,
              },
            ])
          }
        >
          Add milestone
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Save milestones
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Days required</TableCell>
            <TableCell>FE reward</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Message</TableCell>
            <TableCell>Sort</TableCell>
            <TableCell>Active</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {milestones.map((row, idx) => (
            <TableRow key={idx}>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={row.days_required}
                  onChange={(e) => updateRow(idx, { days_required: Number(e.target.value) })}
                  sx={{ width: 100 }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={row.fan_energy_reward}
                  onChange={(e) => updateRow(idx, { fan_energy_reward: Number(e.target.value) })}
                  sx={{ width: 100 }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  value={row.title}
                  onChange={(e) => updateRow(idx, { title: e.target.value })}
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  value={row.message}
                  onChange={(e) => updateRow(idx, { message: e.target.value })}
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  type="number"
                  value={row.sort_order}
                  onChange={(e) => updateRow(idx, { sort_order: Number(e.target.value) })}
                  sx={{ width: 80 }}
                />
              </TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!row.is_active}
                      onChange={(e) => updateRow(idx, { is_active: e.target.checked })}
                      size="small"
                    />
                  }
                  label=""
                />
              </TableCell>
              <TableCell>
                <IconButton
                  size="small"
                  onClick={() => setMilestones((prev) => prev.filter((_, i) => i !== idx))}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default BonusMilestonesPage;
